import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPermanencesAdmin, createPermanence, deletePermanence, updatePermanence, isAdminLoggedIn, logoutAdmin } from '../services/api';
import '../styles/AdminPermanences.css';

function AdminPermanences() {
  const [permanences, setPermanences] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    titre: "Permanence d'accès aux droits et d'accompagnement numérique",
    description: '',
    date: '',
    heureDebut: '',
    heureFin: '',
    lieu: { adresse: '', salle: '', ville: '' },
    capaciteMax: 20,
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAdminLoggedIn()) {
      navigate('/admin/login');
      return;
    }
    fetchPermanences();
  }, [navigate]);

  const fetchPermanences = async () => {
    try {
      const response = await getAllPermanencesAdmin();
      setPermanences(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await updatePermanence(editId, formData);
      } else {
        await createPermanence(formData);
      }
      setShowForm(false);
      setEditMode(false);
      setEditId(null);
      setFormData({
        titre: "Permanence d'accès aux droits et d'accompagnement numérique",
        description: '',
        date: '',
        heureDebut: '',
        heureFin: '',
        lieu: { adresse: '', salle: '', ville: '' },
        capaciteMax: 20,
      });
      fetchPermanences();
    } catch (error) {
      alert(`Erreur lors de ${editMode ? 'la modification' : 'la création'}`);
    }
  };

  const handleEdit = (permanence) => {
    setEditMode(true);
    setEditId(permanence._id);
    setFormData({
      titre: permanence.titre,
      description: permanence.description,
      date: permanence.date.split('T')[0],
      heureDebut: permanence.heureDebut,
      heureFin: permanence.heureFin,
      lieu: permanence.lieu,
      capaciteMax: permanence.capaciteMax,
    });
    setShowForm(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditId(null);
    setShowForm(false);
    setFormData({
      titre: "Permanence d'accès aux droits et d'accompagnement numérique",
      description: '',
      date: '',
      heureDebut: '',
      heureFin: '',
      lieu: { adresse: '', salle: '', ville: '' },
      capaciteMax: 20,
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette permanence ?')) {
      try {
        await deletePermanence(id);
        fetchPermanences();
      } catch (error) {
        alert('Erreur');
      }
    }
  };

  const handleStatusChange = async (id, statut) => {
    try {
      await updatePermanence(id, { statut });
      fetchPermanences();
    } catch (error) {
      alert('Erreur');
    }
  };

  const handleLogout = () => {
    logoutAdmin();
    navigate('/admin/login');
  };

  return (
    <div className="admin-permanences">
      <div className="header">
        <h1>📅 Gestion des Permanences</h1>
        <div>
          <button onClick={() => navigate('/admin/dashboard')} className="btn-secondary">← Dashboard</button>
          <button onClick={() => { if (!showForm) { setShowForm(true); setEditMode(false); } else { handleCancelEdit(); }}} className="btn-primary">
            {showForm ? 'Annuler' : '+ Nouvelle Permanence'}
          </button>
          <button onClick={handleLogout} className="btn-logout">Déconnexion</button>
        </div>
      </div>

      {showForm && (
        <div className="form-card">
          <h2>{editMode ? '✏️ Modifier la permanence' : '➕ Créer une permanence'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Venez rencontrer nos bénévoles..."
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Heure début *</label>
                <input
                  type="time"
                  value={formData.heureDebut}
                  onChange={(e) => setFormData({ ...formData, heureDebut: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Heure fin *</label>
                <input
                  type="time"
                  value={formData.heureFin}
                  onChange={(e) => setFormData({ ...formData, heureFin: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Adresse *</label>
              <input
                type="text"
                value={formData.lieu.adresse}
                onChange={(e) => setFormData({ ...formData, lieu: { ...formData.lieu, adresse: e.target.value } })}
                placeholder="20 Avenue de la Division Leclerc"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Salle</label>
                <input
                  type="text"
                  value={formData.lieu.salle}
                  onChange={(e) => setFormData({ ...formData, lieu: { ...formData.lieu, salle: e.target.value } })}
                  placeholder="Salle Numéro 3"
                />
              </div>
              <div className="form-group">
                <label>Ville *</label>
                <input
                  type="text"
                  value={formData.lieu.ville}
                  onChange={(e) => setFormData({ ...formData, lieu: { ...formData.lieu, ville: e.target.value } })}
                  placeholder="Vénissieux"
                  required
                />
              </div>
              <div className="form-group">
                <label>Capacité max</label>
                <input
                  type="number"
                  value={formData.capaciteMax}
                  onChange={(e) => setFormData({ ...formData, capaciteMax: e.target.value })}
                  min="1"
                />
              </div>
            </div>

            <button type="submit" className="btn-submit">
              {editMode ? '💾 Enregistrer les modifications' : '➕ Créer la permanence'}
            </button>
          </form>
        </div>
      )}

      <div className="permanences-list">
        <h2>Liste des permanences</h2>
        {permanences.length === 0 ? (
          <p>Aucune permanence créée</p>
        ) : (
          <div className="cards-grid">
            {permanences.map((p) => (
              <div key={p._id} className={`permanence-card ${p.statut}`}>
                <div className="card-header">
                  <h3>{p.titre}</h3>
                  <select
                    value={p.statut}
                    onChange={(e) => handleStatusChange(p._id, e.target.value)}
                    className="status-select"
                  >
                    <option value="active">Active</option>
                    <option value="annulee">Annulée</option>
                    <option value="terminee">Terminée</option>
                  </select>
                </div>
                <p className="description">{p.description}</p>
                <div className="info">
                  <div>📅 {new Date(p.date).toLocaleDateString('fr-FR')}</div>
                  <div>🕒 {p.heureDebut} - {p.heureFin}</div>
                  <div>📍 {p.lieu.ville}</div>
                  <div>👥 {p.capaciteMax} places</div>
                </div>
                <div style={{display: 'flex', gap: '10px', marginTop: '10px'}}>
                  <button onClick={() => handleEdit(p)} className="btn-edit">✏️ Modifier</button>
                  <button onClick={() => handleDelete(p._id)} className="btn-delete">🗑️ Supprimer</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPermanences;